import { ArrowSquareOut, Check, Clock, Compass, Medal, Sparkle } from "@phosphor-icons/react";
import { formatCapeTownDate, formatCapeTownDateTime } from "@shared/lib/dates";
import type { ApiItem, CodingBenchmarkEvidence, CodingModelSignal, DashboardResponse } from "@shared/schemas/domain";

interface DailyPulseProps {
  data: DashboardResponse;
  lastVisitAt: string | null;
  onMarkSeen: () => void;
}

function latestAgentRelease(items: ApiItem[]): ApiItem | null {
  return [...items]
    .filter((item) => item.itemType === "coding_tool" && !item.tags.includes("github-activity"))
    .sort((left, right) => right.publishedAt.localeCompare(left.publishedAt))[0] ?? null;
}

function independentResult(model: CodingModelSignal): CodingBenchmarkEvidence | null {
  return model.benchmarks.find(
    (result) => result.provenance === "independent" && result.benchmarkSlug === "swe-rebench",
  ) ?? null;
}

function independentLeader(models: CodingModelSignal[], region?: CodingModelSignal["region"]): CodingModelSignal | null {
  return [...models]
    .filter((model) => !region || model.region === region)
    .filter((model) => independentResult(model))
    .sort((left, right) => (independentResult(left)?.rank ?? 999) - (independentResult(right)?.rank ?? 999))[0] ?? null;
}

export function DailyPulse({ data, lastVisitAt, onMarkSeen }: DailyPulseProps) {
  const leader = independentLeader(data.codingModels);
  const chinaLeader = independentLeader(data.codingModels, "China");
  const sol = data.codingModels.find((model) => model.id === "gpt-5-6-sol-medium") ?? null;
  const solCoding = sol?.benchmarks.find((result) => result.benchmarkSlug === "aa-coding-agent-index") ?? null;
  const emerging = data.codingModels
    .filter((model) => model.region === "China" && model.releaseDate)
    .sort((left, right) => (right.releaseDate ?? "").localeCompare(left.releaseDate ?? ""))[0] ?? null;
  const modelSnapshotDate = data.codingModels.map((model) => model.snapshotDate).sort().reverse()[0] ?? null;
  const release = latestAgentRelease(data.items);
  const newCount = lastVisitAt
    ? data.items.filter((item) => item.publishedAt > lastVisitAt && (item.itemType === "coding_tool" || item.itemType === "model_release" || item.tags.includes("coding-agent"))).length
    : data.items.filter((item) => item.itemType === "coding_tool" || item.itemType === "model_release").length;

  return (
    <section className="pulse-section" aria-labelledby="pulse-heading">
      <div className="pulse-intro">
        <div>
          <p className="eyebrow"><span className="live-dot" /> Daily coding pulse</p>
          <h1 id="pulse-heading">The coding edge,<br /><span>without the noise.</span></h1>
        </div>
        <div className="pulse-intro__aside">
          <p>Fresh benchmark evidence, exact reasoning modes, emerging models, and release movement. Five minutes, once a day.</p>
          <div className="visit-note">
            <span><strong>{newCount}</strong> new coding signal{newCount === 1 ? "" : "s"} since your last check</span>
            <button type="button" className="text-button" onClick={onMarkSeen}><Check aria-hidden="true" /> Mark seen</button>
          </div>
        </div>
      </div>

      <div className="decision-grid">
        {leader && independentResult(leader) && (
          <article className="decision-card decision-card--leader">
            <div className="decision-card__icon"><Medal aria-hidden="true" weight="duotone" /></div>
            <p>Fresh independent leader</p>
            <strong>{leader.model}</strong>
            <div className="decision-card__metric"><b>{independentResult(leader)?.score}%</b><span>SWE-rebench · {independentResult(leader)?.configuration}</span></div>
            <a className="external-link" href={independentResult(leader)?.sourceUrl} target="_blank" rel="noopener noreferrer">Current window <ArrowSquareOut aria-hidden="true" /></a>
          </article>
        )}
        {sol && solCoding && (
          <article className="decision-card">
            <div className="decision-card__icon"><Sparkle aria-hidden="true" weight="duotone" /></div>
            <p>OpenAI coding flagship</p>
            <strong>{sol.model}</strong>
            <div className="decision-card__metric"><b>{solCoding.score}</b><span>AA coding index · max</span></div>
            <a className="external-link" href={solCoding.sourceUrl} target="_blank" rel="noopener noreferrer">Provider launch table <ArrowSquareOut aria-hidden="true" /></a>
          </article>
        )}
        {chinaLeader && independentResult(chinaLeader) && (
          <article className="decision-card">
            <div className="decision-card__icon"><Compass aria-hidden="true" weight="duotone" /></div>
            <p>China signal leader</p>
            <strong>{chinaLeader.model}</strong>
            <div className="decision-card__metric"><b>#{independentResult(chinaLeader)?.rank}</b><span>{independentResult(chinaLeader)?.score}% · open weight</span></div>
            <a className="external-link" href={independentResult(chinaLeader)?.sourceUrl} target="_blank" rel="noopener noreferrer">Independent result <ArrowSquareOut aria-hidden="true" /></a>
          </article>
        )}
        <article className="decision-card decision-card--release">
          <div className="decision-card__icon"><Clock aria-hidden="true" weight="duotone" /></div>
          <p>{emerging ? "Newest model on watch" : "Latest agent release"}</p>
          {emerging ? (
            <>
              <strong>{emerging.model}</strong>
              <div className="decision-card__metric"><b>{emerging.releaseDate ? formatCapeTownDate(emerging.releaseDate) : "New"}</b><span>{emerging.openWeight ? "open weight" : emerging.provider}</span></div>
              {emerging.officialSourceUrl && <a className="external-link" href={emerging.officialSourceUrl} target="_blank" rel="noopener noreferrer">Official model details <ArrowSquareOut aria-hidden="true" /></a>}
            </>
          ) : release ? (
            <>
              <strong>{release.title}</strong>
              <div className="decision-card__metric"><b>{formatCapeTownDate(release.publishedAt)}</b><span>{release.provider ?? release.source.name}</span></div>
              <a className="external-link" href={release.url} target="_blank" rel="noopener noreferrer">Release notes <ArrowSquareOut aria-hidden="true" /></a>
            </>
          ) : (
            <><strong>No verified release yet</strong><span className="muted">The next sync will keep checking official repositories.</span></>
          )}
        </article>
      </div>

      <p className="freshness-line">
        {data.meta.lastSuccessfulRefreshAt ? `Fetched ${formatCapeTownDateTime(data.meta.lastSuccessfulRefreshAt)}` : "No successful production refresh yet"}
        <span>·</span>
        Model evidence verified {modelSnapshotDate ? formatCapeTownDate(modelSnapshotDate) : "not available"}
      </p>
    </section>
  );
}
